import type { ClientOverview, Currency } from '../../types/api';
import { convertUSDToCurrency, convertUSDToCurrencyFormatted } from './currencyConversion';

/**
 * Calculate total retained earnings USD for a client across all units
 * @param clientOverviewData - Array of ClientOverview data for all units
 * @returns Total retained earnings USD value
 */
export function calculateRetainedEarningsUSD(clientOverviewData: ClientOverview[]): number {
  if (!clientOverviewData || clientOverviewData.length === 0) {
    return 0;
  }

  return clientOverviewData.reduce((total, overview) => {
    return total + (overview.retained_earnings_usd || 0);
  }, 0);
}

/**
 * Calculate total retained earnings USD with formatting
 * @param clientOverviewData - Array of ClientOverview data for all units
 * @returns Formatted total retained earnings USD string
 */
export function calculateRetainedEarningsUSDFormatted(clientOverviewData: ClientOverview[]): string {
  const total = calculateRetainedEarningsUSD(clientOverviewData);
  
  if (total === 0) {
    return '0';
  }
  
  // Format with commas for thousands
  return total.toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
}

/**
 * Calculate total retained earnings in a specific currency
 * @param clientOverviewData - Array of ClientOverview data for all units
 * @param targetCurrencyCode - Target currency code
 * @param currencies - Array of currency data
 * @returns Total retained earnings value in target currency
 */
export function calculateRetainedEarningsInCurrency(
  clientOverviewData: ClientOverview[],
  targetCurrencyCode: string,
  currencies: Currency[]
): number {
  const totalUSD = calculateRetainedEarningsUSD(clientOverviewData);
  return convertUSDToCurrency(totalUSD, targetCurrencyCode, currencies);
}

/**
 * Calculate total retained earnings in a specific currency with formatting
 * @param clientOverviewData - Array of ClientOverview data for all units
 * @param targetCurrencyCode - Target currency code
 * @param currencies - Array of currency data
 * @returns Formatted total retained earnings string in target currency
 */
export function calculateRetainedEarningsInCurrencyFormatted(
  clientOverviewData: ClientOverview[],
  targetCurrencyCode: string,
  currencies: Currency[]
): string {
  const totalUSD = calculateRetainedEarningsUSD(clientOverviewData);
  return convertUSDToCurrencyFormatted(totalUSD, targetCurrencyCode, currencies, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    useCurrencySymbol: false
  });
}












